import { CollapsibleFilters } from '../common/CollapsibleFilters';
// components/hr/TrainingProgressReports.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Select,
  Option,
  Input,
  Button,
  Sheet,
  Switch,
  Table,
  Tooltip,
  CircularProgress,
} from '@mui/joy';
import {
  MagnifyingGlass as SearchOutlined,
  CheckCircle as CheckCircleOutlined,
  Hourglass as HourglassEmptyOutlined,
  Clock as PendingOutlined,
  XCircle as CancelOutlined,
  Warning as WarningOutlined,
  TrendUp as TrendingUpOutlined,
  GraduationCap as SchoolOutlined,
  ShieldWarning as ShieldWarningOutlined,
  EnvelopeSimple as EmailOutlined,
  ChatCircleText as SmsOutlined,
} from '@phosphor-icons/react';
import { apiClient } from '../../lib/api';
import { useDebounce } from '../../hooks/useDebounce';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CREDENTIAL_TYPE_LABELS, CREDENTIAL_TYPE_OPTIONS, type CredentialType } from './TrainingEditModal';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';
import type { TeamMemberListItem, TrainingProgress, TrainingProgressStatus } from '../../types/hr';

const PAGE_SIZE = 20;

const statusColors: Record<TrainingProgressStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'primary'> = {
  not_started: 'neutral',
  in_progress: 'primary',
  completed: 'success',
  failed: 'danger',
};

const statusIcons: Record<TrainingProgressStatus, React.ReactNode> = {
  not_started: <PendingOutlined />,
  in_progress: <HourglassEmptyOutlined />,
  completed: <CheckCircleOutlined />,
  failed: <CancelOutlined />,
};

const statusLabels: Record<TrainingProgressStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
};

// ---------------------------------------------------------------------------
// Compliance matrix
// ---------------------------------------------------------------------------

/** Cell statuses from /api/training/progress/compliance-matrix/, worst first. */
type CellStatus =
  | 'expired'
  | 'overdue'
  | 'expiring_soon'
  | 'in_progress'
  | 'not_started'
  | 'complete';

interface ComplianceCell {
  training_item_id: string;
  credential_type: CredentialType | '';
  status: CellStatus;
  progress_id: string | null;
  progress_status: TrainingProgressStatus | null;
  due_date: string | null;
  completed_at: string | null;
  expires_at: string | null;
  days_until_expiry: number | null;
}

interface ComplianceCredential {
  training_item_id: string;
  title: string;
  credential_type: CredentialType | '';
  credential_type_display: string;
  validity_months: number | null;
}

interface ComplianceMember {
  team_member_id: string;
  name: string;
  job_title: string;
  member_type: string;
  status: string;
  is_compliant: boolean;
  cells: ComplianceCell[];
}

interface ComplianceMatrix {
  credentials: ComplianceCredential[];
  members: ComplianceMember[];
  summary: Record<CellStatus, number> & {
    members_total: number;
    members_not_compliant: number;
  };
}

/**
 * Green / amber / red / grey. Expired is solid red because it is the one that
 * takes somebody off a roof; overdue is soft red because the credential has
 * not lapsed yet, the deadline has.
 */
const CELL_STYLES: Record<
  CellStatus,
  { color: 'success' | 'warning' | 'danger' | 'neutral' | 'primary'; variant: 'solid' | 'soft'; label: string; short: string }
> = {
  expired: { color: 'danger', variant: 'solid', label: 'Expired', short: 'EXP' },
  overdue: { color: 'danger', variant: 'soft', label: 'Overdue', short: 'DUE' },
  expiring_soon: { color: 'warning', variant: 'soft', label: 'Expiring soon', short: 'SOON' },
  in_progress: { color: 'primary', variant: 'soft', label: 'In progress', short: '…' },
  not_started: { color: 'neutral', variant: 'soft', label: 'Not started', short: '—' },
  complete: { color: 'success', variant: 'soft', label: 'Current', short: 'OK' },
};

const BLOCKING_STATUSES: CellStatus[] = ['expired', 'overdue'];

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString() : '—';

const useComplianceMatrix = (credentialType: string, includeInactive: boolean) =>
  useQuery({
    // Nested under 'training-progress' so anything that enrolls or completes
    // training and invalidates that prefix redraws the wall too.
    queryKey: ['training-progress', 'compliance-matrix', credentialType, includeInactive],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (credentialType) params.append('credential_type', credentialType);
      if (includeInactive) params.append('include_inactive', 'true');
      const response = await apiClient.get<ComplianceMatrix>(
        `/api/training/progress/compliance-matrix/?${params.toString()}`
      );
      return response.data;
    },
  });

/**
 * Phone and email for every active team member, so a red cell can nudge them.
 * The matrix endpoint deliberately returns no contact details.
 */
const useTeamMemberContacts = () =>
  useQuery({
    // Keyed as a map on purpose. CredentialRequirementsPanel caches the same
    // endpoint under 'team-member-contacts' as a plain array, and React Query
    // keys the cache, not the shape — so sharing the key meant whichever tab
    // loaded first decided what the other one got back. Opening Compliance and
    // then Requirements handed the panel this Map and crashed the page on
    // members.filter.
    queryKey: ['team-member-contact-map'],
    queryFn: async () => {
      const response = await apiClient.get<{ results: TeamMemberListItem[] } | TeamMemberListItem[]>(
        '/api/hr/team-members/?page_size=1000'
      );
      const rows = Array.isArray(response.data) ? response.data : response.data.results;
      return new Map(rows.map((member) => [member.id, member]));
    },
  });

/** A progress row plus the two fields the list serializer gained (is_mandatory, expires_at). */
type ProgressRow = TrainingProgress & { is_mandatory: boolean; expires_at: string | null };

interface ProgressFilters {
  search?: string;
  status?: string;
  is_overdue?: boolean;
  team_member?: string;
  page?: number;
}

const progressParams = (filters: ProgressFilters) => {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.is_overdue !== undefined) params.append('is_overdue', String(filters.is_overdue));
  if (filters.team_member) params.append('team_member', filters.team_member);
  return params;
};

/** One page of progress rows. Paged server-side — the table used to render whatever the first page happened to contain. */
const useTrainingProgressPage = (filters: ProgressFilters) =>
  useQuery({
    queryKey: ['training-progress', 'page', filters],
    queryFn: async () => {
      const params = progressParams(filters);
      params.append('page', String(filters.page ?? 1));
      params.append('page_size', String(PAGE_SIZE));
      const response = await apiClient.get<{ count: number; results: ProgressRow[] }>(
        `/api/training/progress/?${params.toString()}`
      );
      return response.data;
    },
  });

interface ProgressSummary {
  total: number;
  not_started: number;
  in_progress: number;
  completed: number;
  failed: number;
  overdue: number;
  expiring_soon: number;
  expired: number;
  completion_rate: number;
}

/** Summary for the *same* filters as the table, so the cards and the rows agree. */
const useFilteredProgressSummary = (filters: ProgressFilters) =>
  useQuery({
    queryKey: ['training-progress-summary', filters],
    queryFn: async () => {
      const response = await apiClient.get<ProgressSummary>(
        `/api/training/progress/summary/?${progressParams(filters).toString()}`
      );
      return response.data;
    },
  });

// ---------------------------------------------------------------------------

interface CellSelection {
  member: ComplianceMember;
  credential: ComplianceCredential;
  cell: ComplianceCell;
}

const ComplianceCellChip: React.FC<{
  cell: ComplianceCell;
  label?: string;
  onClick: () => void;
}> = ({ cell, label, onClick }) => {
  const style = CELL_STYLES[cell.status];
  return (
    <Tooltip title={label ? `${label}: ${style.label}` : style.label} size="sm">
      <Chip
        size="sm"
        color={style.color}
        variant={style.variant}
        onClick={onClick}
        sx={{ minWidth: 52, justifyContent: 'center', cursor: 'pointer' }}
      >
        {label ? `${label} · ${style.short}` : style.short}
      </Chip>
    </Tooltip>
  );
};

export const TrainingProgressReports: React.FC = () => {
  const isMobile = useIsMobile();

  // Compliance wall filters
  const [credentialFilter, setCredentialFilter] = useState<string>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [onlyNotCompliant, setOnlyNotCompliant] = useState(false);
  const [wallSearch, setWallSearch] = useState('');
  const [selectedCell, setSelectedCell] = useState<CellSelection | null>(null);

  // Detail table filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TrainingProgressStatus | ''>('');
  const [overdueFilter, setOverdueFilter] = useState<boolean | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearch = useDebounce(search, 300);

  // Page 3 of the old result set has nothing to do with the new one.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, overdueFilter]);

  const filters: ProgressFilters = {
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    is_overdue: overdueFilter === '' ? undefined : overdueFilter,
  };

  const { data: matrix, isLoading: loadingMatrix } = useComplianceMatrix(
    credentialFilter,
    includeInactive
  );
  const { data: contacts } = useTeamMemberContacts();
  const { data: summary, isLoading: loadingSummary } = useFilteredProgressSummary(filters);
  const { data: progressPage, isLoading: loadingProgress } = useTrainingProgressPage({
    ...filters,
    page: currentPage,
  });

  const progressRows = progressPage?.results ?? [];
  const totalCount = progressPage?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const wallMembers = useMemo(() => {
    const rows = matrix?.members ?? [];
    const needle = wallSearch.trim().toLowerCase();
    return rows.filter((member) => {
      if (onlyNotCompliant && member.is_compliant) return false;
      if (!needle) return true;
      return (
        member.name.toLowerCase().includes(needle) ||
        (member.job_title || '').toLowerCase().includes(needle)
      );
    });
  }, [matrix, wallSearch, onlyNotCompliant]);

  const credentials = matrix?.credentials ?? [];
  const selectedContact = selectedCell ? contacts?.get(selectedCell.member.team_member_id) : undefined;

  /** A prefilled nudge the manager can send right now. */
  const reminderText = (selection: CellSelection) => {
    const { member, credential, cell } = selection;
    const deadline =
      cell.status === 'expired' || cell.status === 'expiring_soon'
        ? `expires ${formatDate(cell.expires_at)}`
        : `was due ${formatDate(cell.due_date)}`;
    return (
      `Hi ${member.name.split(' ')[0]}, your ${credential.title} ${deadline}. ` +
      'Please sign in to ApexOS and finish it before your next shift on a roof.'
    );
  };

  if (loadingSummary && loadingMatrix) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const detailColumns: ResponsiveColumn<ProgressRow>[] = [
    {
      key: 'team_member',
      sortValue: (row) => row.team_member_name,
      header: 'Team Member',
      mobileVariant: 'header',
      width: '22%',
      render: (row) => (
        <Typography level="title-sm">{row.team_member_name}</Typography>
      ),
    },
    {
      key: 'training',
      sortValue: (row) => row.training_item_title,
      header: 'Training',
      mobileVariant: 'body',
      render: (row) => (
        <Box>
          <Typography level="body-sm">{row.training_item_title}</Typography>
          <Typography level="body-xs" color="neutral">
            {row.training_item_type}
            {row.is_mandatory ? ' · mandatory' : ''}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      sortValue: (row) => row.status,
      header: 'Status',
      width: '16%',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            size="sm"
            color={statusColors[row.status]}
            variant="soft"
            startDecorator={statusIcons[row.status]}
          >
            {statusLabels[row.status]}
          </Chip>
          {row.is_overdue && (
            <Chip size="sm" color="danger" variant="solid">
              Overdue
            </Chip>
          )}
        </Box>
      ),
    },
    {
      key: 'progress',
      sortValue: (row) => row.watch_percentage,
      header: 'Progress',
      width: '12%',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            determinate
            value={row.watch_percentage}
            sx={{ flex: 1, maxWidth: 60 }}
          />
          <Typography level="body-xs">{row.watch_percentage}%</Typography>
        </Box>
      ),
    },
    {
      key: 'due_date',
      sortValue: (row) => row.due_date ? new Date(row.due_date) : null,
      header: 'Due',
      width: '11%',
      render: (row) => (
        <Typography level="body-xs" color={row.is_overdue ? 'danger' : 'neutral'}>
          {formatDate(row.due_date)}
        </Typography>
      ),
    },
    {
      key: 'expires_at',
      sortValue: (row) => row.expires_at ? new Date(row.expires_at) : null,
      header: 'Expires',
      width: '11%',
      render: (row) => (
        <Typography level="body-xs" color="neutral">
          {formatDate(row.expires_at)}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ------------------------------------------------------------------ */}
      {/* Compliance wall                                                     */}
      {/* ------------------------------------------------------------------ */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <ShieldWarningOutlined color="var(--joy-palette-danger-500)" />
          <Typography level="title-lg">Compliance Wall</Typography>
        </Box>
        <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
          Who is not clear to be on a roof right now. Red is an expired or overdue
          credential; tap a cell to see the dates and send a reminder.
        </Typography>

        {/* Compliance KPIs */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Card>
            <CardContent>
              <Typography level="body-xs" color="neutral">Not Clear to Work</Typography>
              <Typography level="h3" color={matrix?.summary.members_not_compliant ? 'danger' : 'success'}>
                {matrix?.summary.members_not_compliant ?? 0}
                <Typography level="body-sm" color="neutral">
                  {' '}/ {matrix?.summary.members_total ?? 0}
                </Typography>
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography level="body-xs" color="neutral">Expired Credentials</Typography>
              <Typography level="h3" color="danger">{matrix?.summary.expired ?? 0}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography level="body-xs" color="neutral">Overdue</Typography>
              <Typography level="h3" color="danger">{matrix?.summary.overdue ?? 0}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography level="body-xs" color="neutral">Expiring in 30 Days</Typography>
              <Typography level="h3" color="warning">{matrix?.summary.expiring_soon ?? 0}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography level="body-xs" color="neutral">Current</Typography>
              <Typography level="h3" color="success">{matrix?.summary.complete ?? 0}</Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Wall filters */}
        <CollapsibleFilters><Card sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Input
              placeholder="Find a crew member..."
              startDecorator={<SearchOutlined />}
              value={wallSearch}
              onChange={(e) => setWallSearch(e.target.value)}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 240px' } }}
            />
            <Select
              placeholder="Credential"
              value={credentialFilter}
              onChange={(_, value) => setCredentialFilter((value as string) || '')}
              sx={{ flex: { xs: '1 1 100%', sm: '0 0 200px' } }}
            >
              <Option value="">All Credentials</Option>
              {CREDENTIAL_TYPE_OPTIONS.map(([value, label]) => (
                <Option key={value} value={value}>{label}</Option>
              ))}
            </Select>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={onlyNotCompliant}
                onChange={(e) => setOnlyNotCompliant(e.target.checked)}
              />
              <Typography level="body-sm">Only not clear</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              <Typography level="body-sm">Include inactive</Typography>
            </Box>
          </Box>
        </Card>
</CollapsibleFilters>

        {/* The wall itself */}
        <Card>
          {loadingMatrix ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : credentials.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography level="body-lg">No credentials to track yet</Typography>
              <Typography level="body-sm" color="neutral">
                Tag a training item with a safety credential (OSHA 10, Fall Protection,
                …) in the Library and it becomes a column here.
              </Typography>
            </Box>
          ) : wallMembers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography level="body-lg">No team members match</Typography>
              <Typography level="body-sm" color="neutral">
                {onlyNotCompliant ? 'Everybody is clear to work.' : 'Try adjusting your filters.'}
              </Typography>
            </Box>
          ) : isMobile ? (
            // One card per person: 200 rows of person x item never fit a phone.
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
              {wallMembers.map((member) => (
                <Sheet
                  key={member.team_member_id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 'sm',
                    borderLeft: '4px solid',
                    borderLeftColor: member.is_compliant ? 'success.400' : 'danger.500',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                    <Typography level="title-sm" sx={{ flex: 1 }}>{member.name}</Typography>
                    {!member.is_compliant && (
                      <Chip size="sm" color="danger" variant="solid">Not clear</Chip>
                    )}
                  </Box>
                  {member.job_title && (
                    <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                      {member.job_title}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {member.cells.map((cell, index) => (
                      <ComplianceCellChip
                        key={cell.training_item_id}
                        cell={cell}
                        label={
                          credentials[index]?.credential_type
                            ? CREDENTIAL_TYPE_LABELS[credentials[index].credential_type as CredentialType]
                            : credentials[index]?.title
                        }
                        onClick={() =>
                          setSelectedCell({ member, credential: credentials[index], cell })
                        }
                      />
                    ))}
                  </Box>
                </Sheet>
              ))}
            </Box>
          ) : (
            <Sheet sx={{ overflowX: 'auto' }}>
              <Table
                stickyHeader
                sx={{
                  '--Table-headerUnderlineThickness': '1px',
                  minWidth: 320 + credentials.length * 120,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: 260 }}>Team Member</th>
                    {credentials.map((credential) => (
                      <th key={credential.training_item_id} style={{ width: 120 }}>
                        <Typography level="body-xs" sx={{ fontWeight: 'lg' }}>
                          {credential.credential_type_display || credential.title}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {credential.validity_months
                            ? `${credential.validity_months} mo`
                            : 'no expiry'}
                        </Typography>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {wallMembers.map((member) => (
                    <tr key={member.team_member_id}>
                      <td>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 4,
                              height: 28,
                              borderRadius: 2,
                              bgcolor: member.is_compliant ? 'success.400' : 'danger.500',
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography level="body-sm" fontWeight="md" noWrap>
                              {member.name}
                            </Typography>
                            {member.job_title && (
                              <Typography level="body-xs" color="neutral" noWrap>
                                {member.job_title}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </td>
                      {member.cells.map((cell, index) => (
                        <td key={cell.training_item_id}>
                          <ComplianceCellChip
                            cell={cell}
                            onClick={() =>
                              setSelectedCell({ member, credential: credentials[index], cell })
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          )}

          {credentials.length > 0 && wallMembers.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', p: 1.5, pt: 0 }}>
              {(Object.keys(CELL_STYLES) as CellStatus[]).map((status) => (
                <Chip
                  key={status}
                  size="sm"
                  color={CELL_STYLES[status].color}
                  variant={CELL_STYLES[status].variant}
                >
                  {CELL_STYLES[status].label}
                </Chip>
              ))}
            </Box>
          )}
        </Card>
      </Box>

      <Divider />

      {/* ------------------------------------------------------------------ */}
      {/* All training records                                                */}
      {/* ------------------------------------------------------------------ */}
      <Box>
        <Typography level="title-lg" sx={{ mb: 0.5 }}>All Training Records</Typography>
        <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
          Every assignment, including training that does not grant a credential.
        </Typography>

        {/* Summary Cards — these count the same filtered set as the table below */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SchoolOutlined color="var(--joy-palette-primary-500)" />
                <Typography level="body-xs" color="neutral">Assignments</Typography>
              </Box>
              <Typography level="h3">{summary?.total || 0}</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleOutlined color="var(--joy-palette-success-500)" />
                <Typography level="body-xs" color="neutral">Completed</Typography>
              </Box>
              <Typography level="h3" color="success">{summary?.completed || 0}</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HourglassEmptyOutlined color="var(--joy-palette-primary-500)" />
                <Typography level="body-xs" color="neutral">In Progress</Typography>
              </Box>
              <Typography level="h3" color="primary">{summary?.in_progress || 0}</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningOutlined color="var(--joy-palette-danger-500)" />
                <Typography level="body-xs" color="neutral">Overdue</Typography>
              </Box>
              <Typography level="h3" color="danger">{summary?.overdue || 0}</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpOutlined color="var(--joy-palette-success-500)" />
                <Typography level="body-xs" color="neutral">Completion Rate</Typography>
              </Box>
              <Typography level="h3">{summary?.completion_rate?.toFixed(0) || 0}%</Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Filters */}
        <CollapsibleFilters><Card sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Input
              placeholder="Search by name or training..."
              startDecorator={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' } }}
            />
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={(_, value) => setStatusFilter(value as TrainingProgressStatus | '')}
              sx={{ flex: { xs: '1 1 calc(50% - 6px)', sm: '0 0 160px' } }}
            >
              <Option value="">All Statuses</Option>
              <Option value="not_started">Not Started</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="completed">Completed</Option>
              <Option value="failed">Failed</Option>
            </Select>
            <Select
              placeholder="Overdue"
              value={overdueFilter === '' ? '' : overdueFilter ? 'true' : 'false'}
              onChange={(_, value) => setOverdueFilter(value === '' ? '' : value === 'true')}
              sx={{ flex: { xs: '1 1 calc(50% - 6px)', sm: '0 0 160px' } }}
            >
              <Option value="">All</Option>
              <Option value="true">Overdue Only</Option>
              <Option value="false">Not Overdue</Option>
            </Select>
          </Box>
        </Card></CollapsibleFilters>

        <Card>
          {loadingProgress ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveTable
              rows={progressRows}
              getRowKey={(row) => row.id}
              columns={detailColumns}
              emptyState={
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography level="body-lg">No training progress found</Typography>
                  <Typography level="body-sm" color="neutral">
                    {search || statusFilter || overdueFilter !== ''
                      ? 'Try adjusting your filters'
                      : 'Training assignments will appear here once team members are assigned training'}
                  </Typography>
                </Box>
              }
            />
          )}
        </Card>

        {/* Pagination */}
        {totalCount > 0 && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
            }}
          >
            <Typography level="body-sm" color="neutral">
              {`Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} records`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((page) => page - 1)}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((page) => page + 1)}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Cell detail + reminder */}
      <Modal open={!!selectedCell} onClose={() => setSelectedCell(null)}>
        <ModalDialog sx={{ width: { xs: 'auto', sm: 440 } }}>
          <ModalClose />
          {selectedCell && (
            <>
              <DialogTitle>{selectedCell.member.name}</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography level="title-sm" sx={{ flex: 1 }}>
                      {selectedCell.credential?.title}
                    </Typography>
                    <Chip
                      size="sm"
                      color={CELL_STYLES[selectedCell.cell.status].color}
                      variant={CELL_STYLES[selectedCell.cell.status].variant}
                    >
                      {CELL_STYLES[selectedCell.cell.status].label}
                    </Chip>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Box>
                      <Typography level="body-xs" color="neutral">Due</Typography>
                      <Typography level="body-sm">{formatDate(selectedCell.cell.due_date)}</Typography>
                    </Box>
                    <Box>
                      <Typography level="body-xs" color="neutral">Completed</Typography>
                      <Typography level="body-sm">{formatDate(selectedCell.cell.completed_at)}</Typography>
                    </Box>
                    <Box>
                      <Typography level="body-xs" color="neutral">Expires</Typography>
                      <Typography level="body-sm">{formatDate(selectedCell.cell.expires_at)}</Typography>
                    </Box>
                    <Box>
                      <Typography level="body-xs" color="neutral">Days to expiry</Typography>
                      <Typography level="body-sm">
                        {selectedCell.cell.days_until_expiry ?? '—'}
                      </Typography>
                    </Box>
                  </Box>

                  {BLOCKING_STATUSES.includes(selectedCell.cell.status) && (
                    <Typography level="body-xs" color="danger">
                      This credential is not current — {selectedCell.member.name} should not be
                      on a roof until it is renewed.
                    </Typography>
                  )}

                  <Divider />

                  {/* Nightly reminders are handled by the training reminder task;
                      this is the manual nudge for the ones that cannot wait. */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedContact?.email && (
                      <Button
                        component="a"
                        size="sm"
                        startDecorator={<EmailOutlined />}
                        href={`mailto:${selectedContact.email}?subject=${encodeURIComponent(
                          `Action needed: ${selectedCell.credential?.title ?? 'training'}`
                        )}&body=${encodeURIComponent(reminderText(selectedCell))}`}
                      >
                        Email reminder
                      </Button>
                    )}
                    {selectedContact?.phone && (
                      <Button
                        component="a"
                        size="sm"
                        variant="soft"
                        startDecorator={<SmsOutlined />}
                        href={`sms:${selectedContact.phone}?&body=${encodeURIComponent(
                          reminderText(selectedCell)
                        )}`}
                      >
                        Text reminder
                      </Button>
                    )}
                  </Box>
                  {!selectedContact?.email && !selectedContact?.phone && (
                    <Typography level="body-xs" color="neutral">
                      No email or phone on this team member's record.
                    </Typography>
                  )}
                </Box>
              </DialogContent>
            </>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default TrainingProgressReports;
